import { Link } from "components/ui/link";
import { ChevronRight } from "lucide-react";
import React, { useCallback, useEffect, useState, useTransition } from "react";
import {
  graphql,
  PreloadedQuery,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";
import { useParams } from "react-router";
import { ContentSuspense } from "src/components/ui/content-suspense";
import { UITourBanner } from "src/components/ui/UITourBanner";
import { usePusherEvent } from "src/hooks/use-pusher-event";

import { DigestDetailsQuery } from "./__generated__/DigestDetailsQuery.graphql";
import DigestJobDetails from "./components/DigestJobDetails";

export interface SectionProgressType {
  activeSectionIndex: number;
  totalSections: number;
  sectionName: string;
}

interface DigestSectionEventPayloadType {
  section_index: number;
  total_sections: number;
  section_name: string;
}

const DigestDetailsQueryDocument = graphql`
  query DigestDetailsQuery($id: GlobalID!) {
    me {
      ...appMe
    }
    node(id: $id) {
      ... on GraphQLJobRun {
        ...DigestJobDetails_jobRun
        name
        job {
          id
          name
        }
      }
    }
  }
`;

function DigestContent({
  queryRef,
  sectionProgress,
}: {
  queryRef: PreloadedQuery<DigestDetailsQuery>;
  sectionProgress: SectionProgressType | null;
}) {
  const [onboardingStep, setOnboardingStep] = useState<number | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stepParam = urlParams.get("onboarding");
    if (stepParam) {
      const step = parseInt(stepParam, 10);
      setOnboardingStep(isNaN(step) ? null : step);
    }
  }, []);

  const isOnboarding = onboardingStep !== null;

  const data = usePreloadedQuery<DigestDetailsQuery>(
    DigestDetailsQueryDocument,
    queryRef
  );

  const jobRun = data.node;

  if (!jobRun) {
    return (
      <div className="p-8 text-center">
        <h3 className="text-xl font-semibold text-slate-100 mb-2">
          Job run not found
        </h3>
      </div>
    );
  }

  const job = jobRun.job;
  const onboardingParam = onboardingStep ? `?onboarding=${onboardingStep}` : "";

  return (
    <div className="p-6">
      {isOnboarding && (
        <UITourBanner
          text="Excellent! Here are the results from your digest run. Review the findings below."
          showBackButton={true}
          onboardingStep={onboardingStep}
        />
      )}

      <div className="space-y-3">
        <div className="text-sm text-slate-400">
          <div className="flex items-center">
            <Link
              to="/digests"
              className="text-primary hover:text-primary/80 transition-colors no-underline hover:no-underline"
            >
              Digest Agents
            </Link>
            <ChevronRight className="mx-2 w-3 h-3" />
            <Link
              to={`/digests/job/${job?.id}${onboardingParam}`}
              className="text-primary hover:text-primary/80 transition-colors no-underline hover:no-underline"
            >
              {job?.name || "Digest"}
            </Link>
            <ChevronRight className="mx-2 w-3 h-3" />
            <span className="text-slate-300">{jobRun.name || "Digest"}</span>
          </div>
        </div>

        <div>
          <DigestJobDetails
            jobRun={jobRun}
            sectionProgress={sectionProgress}
          />
        </div>
      </div>
    </div>
  );
}

const DigestDetails: React.FC = () => {
  const { digestJobId } = useParams<{ digestJobId: string }>();
  const [queryRef, loadQuery] = useQueryLoader<DigestDetailsQuery>(
    DigestDetailsQueryDocument
  );
  const [, startTransition] = useTransition();
  const [sectionProgress, setSectionProgress] =
    useState<SectionProgressType | null>(null);

  const refetch = useCallback(() => {
    if (digestJobId) {
      startTransition(() => {
        loadQuery({ id: digestJobId }, { fetchPolicy: "network-only" });
      });
    }
  }, [digestJobId, loadQuery, startTransition]);

  usePusherEvent<{ id: string }>({
    channelId: digestJobId,
    eventType: "JOB_RUN_UPDATED",
    onEvent: refetch,
  });

  usePusherEvent<DigestSectionEventPayloadType>({
    channelId: digestJobId,
    eventType: "DIGEST_SECTION_STARTED",
    onEvent: useCallback((data) => {
      setSectionProgress({
        activeSectionIndex: data.section_index,
        totalSections: data.total_sections,
        sectionName: data.section_name,
      });
    }, []),
  });

  usePusherEvent<DigestSectionEventPayloadType>({
    channelId: digestJobId,
    eventType: "DIGEST_SECTION_COMPLETED",
    onEvent: useCallback(
      (data) => {
        if (data.section_index >= data.total_sections - 1) {
          setSectionProgress(null);
        }
        refetch();
      },
      [refetch]
    ),
  });

  React.useEffect(() => {
    if (digestJobId) {
      loadQuery({ id: digestJobId });
    }
  }, [digestJobId, loadQuery]);

  if (!digestJobId) {
    return <div>Invalid digest job ID</div>;
  }

  return (
    <div className="h-full w-full overflow-y-auto custom-scrollbar">
      {queryRef ? (
        <ContentSuspense>
          <DigestContent
            queryRef={queryRef}
            sectionProgress={sectionProgress}
          />
        </ContentSuspense>
      ) : (
        <div />
      )}
    </div>
  );
};

export default DigestDetails;
